var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import * as React from 'react';
import { useRef, useCallback, useState, useMemo, } from 'react';
import { useTranslate } from 'react-admin';
import { Popover, styled, alpha, } from '@mui/material';
import { SearchInput } from './SearchInput';
import { SearchResultsPanel } from './SearchResultsPanel';
import { SearchResultContextProvider } from './SearchResultContext';
import { SearchHistoryPanel } from './SearchHistoryPanel';
import { useSearchController, } from './useSearchController';
/**
 * A component which displays an input, calls the dataProvider `search` method
 * when the input changes and shows the search results inside a Material UI
 * [`<PopOver>`](https://mui.com/components/popover/).
 *
 * By default, it will group the search result items by resource and show their
 * `content.label` and `content.description`.
 *
 * @param props {SearchProps}
 * @param props.children {ReactElement} A React Element which will be rendered inside the Material UI `<Popover>`
 * @param props.queryOptions {QueryOptions} `react-query` options
 *
 * @example <caption>Here's how to include it inside a custom `<AppBar>` component</caption>
 * import { Admin, AppBar, Layout, Resource } from 'react-admin';
 * import { Typography } from '@mui/material';
 * import { Search } from '@react-admin/ra-search';
 *
 * const MyAppbar = (props) => (
 *     <AppBar {...props}>
 *         <Typography
 *             variant="h6"
 *             color="inherit"
 *             sx={{
 *                 flex: 1,
 *                 textOverflow: 'ellipsis',
 *                 whiteSpace: 'nowrap',
 *                 overflow: 'hidden',
 *             }}
 *             id="react-admin-title"
 *         />
 *         <Search />
 *     </AppBar>
 * );
 *
 *
 * const MyLayout = (props) => (
 *     <Layout {...props} appBar={MyAppbar} />
 * );
 *
 * export const App = () => (
 *     <Admin dataProvider={searchDataProvider} layout={MyLayout}>
 *         {...}
 *     </Admin>
 * );
 *
 * @example <caption>Customizing the <Popover> Content</caption>
 * import { Admin, AppBar, Layout, Resource } from 'react-admin';
 * import { Typography } from '@mui/material';
 * import { Search } from '@react-admin/ra-search';
 *
 * const MySearchResultsPanel = () => {
 *     const { data, onClose } = useSearchResults();
 *
 *     return (
 *         <ul>
 *             {data.map(item => (
 *                 <li key={item.id}>
 *                     <Link to={item.url} onClick={onClose}>
 *                         <strong>{item.content.label}</strong>
 *                     </Link>
 *                     <p>{item.content.description}</p>
 *                 </li>
 *             ))}
 *         </ul>
 *     );
 * };
 *
 * const MyAppbar = (props) => (
 *     <AppBar {...props}>
 *         <Typography
 *             variant="h6"
 *             color="inherit"
 *             sx={{
 *                 flex: 1,
 *                 textOverflow: 'ellipsis',
 *                 whiteSpace: 'nowrap',
 *                 overflow: 'hidden',
 *             }}
 *             id="react-admin-title"
 *         />
 *         <Search>
 *             <CustomSearchResultsPanel />
 *         </Search>
 *     </AppBar>
 * );
 *
 * const MyLayout = (props) => (
 *     <Layout {...props} appBar={MyAppbar} />
 * );
 *
 * export const App = () => (
 *     <Admin
 *         dataProvider={searchDataProvider}
 *         layout={MyLayout}
 *     >
 *         {...}
 *     </Admin>
 * );
 *
 * @example <caption>Customizing the <SearchResultItem> Component</caption>
 * import { Admin, AppBar, Layout, Resource } from 'react-admin';
 * import { Typography } from '@mui/material';
 * import { Search, SearchResultsPanel, SearchResultItem } from '@react-admin/ra-search';
 *
 * const MyAppbar = (props) => (
 *     <AppBar {...props}>
 *         <Typography
 *             variant="h6"
 *             color="inherit"
 *             sx={{
 *                 flex: 1,
 *                 textOverflow: 'ellipsis',
 *                 whiteSpace: 'nowrap',
 *                 overflow: 'hidden',
 *             }}
 *             id="react-admin-title"
 *         />
 *         <Search>
 *             <SearchResultsPanel>
 *                 <SearchResultItem
 *                     label={(record) => (
 *                         <>
 *                             {record.type === 'artists' ? (
 *                                 <PersonIcon />
 *                             ) : (
 *                                 <MusicIcon />
 *                             )}
 *                             <span>{record.content.label}</span>
 *                         </>
 *                     )}
 *                 />
 *             </SearchResultsPanel>
 *         </Search>
 *     </AppBar>
 * );
 *
 * const MyLayout = (props) => (
 *     <Layout {...props} appBar={MyAppbar} />
 * );
 *
 * export const App = () => (
 *     <Admin dataProvider={dataProvider} layout={MyLayout}>
 *         {...}
 *     </Admin>
 * );
 */
export var Search = function (props) {
    var _a = props.children, children = _a === void 0 ? React.createElement(SearchResultsPanel, null) : _a, options = props.options, queryOptions = props.queryOptions, _b = props.wait, wait = _b === void 0 ? 500 : _b, _c = props.historySize, historySize = _c === void 0 ? 5 : _c, _d = props.isInAppBar, isInAppBar = _d === void 0 ? true : _d;
    var ref = useRef();
    var inputRef = useRef();
    var translate = useTranslate();
    var _e = useState(false), open = _e[0], setOpen = _e[1];
    var _f = useState([]), history = _f[0], setHistory = _f[1];
    var _g = useSearchController({
        options: options,
        wait: wait,
        queryOptions: __assign(__assign({}, queryOptions), { onSuccess: function (data) {
                setOpen(true);
                (queryOptions === null || queryOptions === void 0 ? void 0 : queryOptions.onSuccess) && queryOptions.onSuccess(data);
            } }),
    }), searchData = _g.searchData, query = _g.query, doSearch = _g.doSearch;
    var onClose = useCallback(function (addQueryToHistory) {
        if (addQueryToHistory === void 0) { addQueryToHistory = true; }
        setOpen(false);
        if (addQueryToHistory && query && !history.includes(query)) {
            var newHistory = __spreadArray([query], history, true);
            newHistory.splice(historySize, newHistory.length > historySize
                ? newHistory.length - historySize
                : 0);
            setHistory(newHistory);
        }
    }, [history, historySize, query, setHistory]);
    var handleChange = useCallback(function (event) {
        var query = event.target
            ? event.target.value
            : event;
        doSearch(query);
        if (!query && history.length === 0) {
            onClose(false);
        }
    }, [onClose, history.length, doSearch]);
    var handleFocus = useCallback(function () {
        if ((query && searchData.isLoaded) || history.length > 0) {
            setOpen(true);
        }
    }, [query, searchData, history]);
    var contextValue = useMemo(function () { return (__assign(__assign({}, searchData), { onClose: onClose })); }, [searchData, onClose]);
    return (React.createElement(Root, { className: isInAppBar ? classes.isInAppBar : null },
        React.createElement(SearchInput, __assign({ ref: ref, inputRef: inputRef, onChange: handleChange, onFocus: handleFocus, value: query }, sanitizeInputProps(props))),
        React.createElement(Popover, { anchorEl: ref.current, anchorReference: "anchorEl", anchorOrigin: anchorOrigin, transformOrigin: transformOrigin, elevation: 4, disableAutoFocus: true, disableEnforceFocus: true, disableRestoreFocus: true, disablePortal: true, open: open, onClose: onClose }, query ? (searchData.error ? (translate('ra.notification.http_error', {
            _: 'Server communication error',
        })) : (React.createElement(SearchResultContextProvider, { value: contextValue }, children))) : history.length > 0 ? (React.createElement(SearchHistoryPanel, { history: history, onSelect: doSearch })) : null)));
};
var anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'right',
};
var transformOrigin = {
    vertical: 'top',
    horizontal: 'right',
};
var PREFIX = 'RaSearch';
var classes = {
    isInAppBar: "".concat(PREFIX, "-isInAppBar"),
};
var Root = styled('div', {
    name: PREFIX,
    overridesResolver: function (props, styles) { return styles.root; },
})(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            '& .RaSearchInput-root': {
                width: '100%',
            }
        },
        _b["&.".concat(classes.isInAppBar)] = {
            '& .RaSearchInput-root': {
                backgroundColor: alpha(theme.palette.background.default, 0.15),
                '&:hover': {
                    backgroundColor: alpha(theme.palette.common.white, 0.15),
                },
                '&:focus-within': {
                    backgroundColor: alpha(theme.palette.common.white, 0.2),
                },
            },
            '& .RaSearchInput-inputBase': {
                background: 'none',
                '&:hover': {
                    background: alpha(theme.palette.common.white, 0.15),
                },
            },
            '& .RaSearchInput-input': {
                width: 120,
            },
        },
        _b);
});
var sanitizeInputProps = function (_a) {
    var historySize = _a.historySize, rest = __rest(_a, ["historySize"]);
    return rest;
};
